import { pathForApp } from "../runtime/stage-router.js";
const BALANCE_QUERY = "wallet.balance.v1";
const WALLET_PATH = pathForApp("wallet");
/**
 * Port of the original skin's bottom-left ambient wallet pill (`SKIN/index.html` lines
 * 163-166, `#walletStatus`/`.mirror-wallet`/`.pmenu-delta`) — mounted as a SIBLING of
 * `<node-shell-pmenu>` in `app-shell.ts`'s shadow skeleton, not nested inside `.pmenu`
 * (the original never puts it there either: both are fixed-position children of `<body>`,
 * positioned independently by the vendored `legacy-numbered.css` — see `.wallet-status`'s
 * own `position:fixed;left:...;bottom:13px`).
 *
 * Reads the same real `wallet.balance.v1` live query `wallet-stage.ts` binds its own hero
 * balance to (confirmed live there — see that file's own doc comment for the grep that
 * proves it, not a fabricated query name). Renders the vendored placeholder ("— sats")
 * until a real balance resolves, exactly like `wallet-stage.ts`'s own `data-wallet-balance`
 * placeholder — never a fake number.
 *
 * Subscription discipline mirrors `sync-indicator.ts`: the `client` setter tears down any
 * existing subscription and only re-subscribes `if (this.isConnected)` — app-shell.ts
 * assigns `.client` unconditionally near the top of its own `#start()`, before its
 * generation guard, so a start that races a disconnect (see app-shell.test.ts's "does not
 * create subscriptions after disconnecting during an in-flight start") must not leak a
 * `wallet.balance.v1` subscription just because the setter ran on an already-disconnected
 * element.
 *
 * Does not implement the `.ws-dark` theme flip yet (`legacy-numbered.css`'s
 * `.wallet-status.ws-dark` variant) — that depends on dark mode / the `waves` stage, both
 * out of scope for this unit.
 */
export class NodeWalletStatusElement extends HTMLElement {
    #balanceMsat;
    #client;
    #generation = 0;
    #unsubscribe;
    connectedCallback() {
        this.render();
        this.addEventListener("click", this.#handleActivate);
        this.addEventListener("keydown", this.#handleActivate);
        this.#subscribe();
    }
    disconnectedCallback() {
        this.removeEventListener("click", this.#handleActivate);
        this.removeEventListener("keydown", this.#handleActivate);
        this.#teardown();
    }
    set client(value) {
        this.#client = value;
        this.#teardown();
        if (this.isConnected) {
            this.#subscribe();
        }
    }
    get client() {
        return this.#client;
    }
    render() {
        this.innerHTML = `
      <div class="wallet-status">
        <span class="mirror-wallet" role="button" tabindex="0" title="Wallet — balance &amp; activity">${balanceText(this.#balanceMsat)}</span>
        <span class="pmenu-delta"></span>
      </div>
    `;
    }
    #subscribe() {
        const client = this.#client;
        if (!client || this.#unsubscribe !== undefined) {
            return;
        }
        const generation = ++this.#generation;
        this.#unsubscribe = client.liveQuery(BALANCE_QUERY, {}, (diff) => {
            if (generation !== this.#generation)
                return;
            const next = diff.updated[0] ?? diff.added[0];
            if (next !== undefined) {
                this.#applyBalance(next.balanceMsat);
            }
        });
        void client.query(BALANCE_QUERY, {})
            .then((rows) => {
            if (generation !== this.#generation)
                return;
            this.#applyBalance(rows[0]?.balanceMsat);
        })
            .catch(() => {
            // Offline/error: leave the "— sats" placeholder rendered above in place.
        });
    }
    #teardown() {
        this.#generation += 1;
        this.#unsubscribe?.();
        this.#unsubscribe = undefined;
    }
    #applyBalance(balanceMsat) {
        this.#balanceMsat = typeof balanceMsat === "number" && Number.isFinite(balanceMsat) ? balanceMsat : undefined;
        this.render();
    }
    #handleActivate = (event) => {
        if (event instanceof KeyboardEvent && event.key !== "Enter" && event.key !== " ") {
            return;
        }
        const target = event.target;
        if (!(target instanceof Element) || !target.closest(".mirror-wallet")) {
            return;
        }
        if (event instanceof KeyboardEvent) {
            event.preventDefault();
        }
        this.dispatchEvent(new CustomEvent("navigate", {
            bubbles: true,
            composed: true,
            detail: { path: WALLET_PATH },
        }));
    };
}
function balanceText(balanceMsat) {
    if (balanceMsat === undefined) {
        return "— sats";
    }
    return `${Math.round(balanceMsat / 1000).toLocaleString()} sats`;
}
//# sourceMappingURL=wallet-status.js.map